import React, { useState, useEffect } from "react";
import { Table, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaEdit, FaTrash } from "react-icons/fa";
import authService from "../../../utils/authService";

interface CollegeData {
  college_id: string;
  college_name: string;
  center_name: string;
  college_code: string;
  del_row: boolean;
  college_logo?: string | null;
}

const Colleges: React.FC = () => {
  const [search, setSearch] = useState("");
  const [colleges, setColleges] = useState<CollegeData[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("new");
  const [modalNewMessage, setModalNewMessage] = useState<string>("");
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [formData, setFormData] = useState<{
    college_id: string;
    college_name: string;
    center_name: string;
    college_code: string;
  }>({
    college_id: "",
    college_name: "",
    center_name: "",
    college_code: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedCollege, setSelectedCollege] = useState<CollegeData | null>(null);

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const response = await authService.get("/admin/colleges/details/");
      if (response.data && response.data.colleges) {
        setColleges(response.data.colleges as CollegeData[]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching colleges:", error);
      setError("Failed to fetch colleges");
      setLoading(false);
    }
  };

  const handleModal = (type: string, college?: CollegeData): void => {
    setModalType(type);
    if (type === "new") {
      setFormData({
        college_id: "",
        college_name: "",
        center_name: "",
        college_code: "",
      });
    } else if (college) {
      setFormData({
        college_id: college.college_id,
        college_name: college.college_name,
        center_name: college.center_name,
        college_code: college.college_code,
      });
    }
    setShowModal(true);
    setError("");
  };

  const padCollegeCode = (code: string): string => {
    if (code.length === 0) return "";
    if (code.length >= 4) return code.substring(0, 4);
    return code.padEnd(4, "X");
  };

  const handleCollegeCodeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let value = e.target.value.toUpperCase();
    // Only allow letters (A-Z), no numbers or special characters
    value = value.replace(/[^A-Z]/g, "");
    // Limit to 4 characters
    if (value.length > 4) {
      value = value.substring(0, 4);
    }
    setFormData({
      ...formData,
      college_code: value,
    });
    setError("");
  };

  const handleSave = async (): Promise<void> => {
    // Validation
    if (!formData.college_name || !formData.center_name || !formData.college_code) {
      setError("All fields are required");
      return;
    }

    if (formData.college_code.length === 0) {
      setError("College code is required");
      return;
    }

    // Check for duplicate college_code (only for new colleges)
    if (modalType === "new") {
      const isCodeTaken = colleges.some(
        (college) =>
          college.college_code.toLowerCase() === formData.college_code.toLowerCase() &&
          !college.del_row
      );
      if (isCodeTaken) {
        setError("College code already exists");
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      // Pad college_code if needed
      const paddedCode = padCollegeCode(formData.college_code);

      const json = {
        college_id: modalType === "new" ? "" : formData.college_id,
        college_name: formData.college_name.trim(),
        center_name: formData.center_name.trim(),
        college_code: paddedCode,
      };

      const response = await authService.post("/admin/add_college/", json);

      if (response.status === 409) {
        setError("College code already exists");
        setLoading(false);
        return;
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(`Failed to ${modalType === "edit" ? "update" : "save"} college`);
      }

      await fetchColleges();
      setShowModal(false);
      setModalNewMessage(
        modalType === "edit" ? "College updated successfully" : "College created successfully"
      );
      setShowUpdateModal(true);
      setLoading(false);
    } catch (error: any) {
      if (error.response?.status === 409) {
        setError("College code already exists");
      } else {
        setError(`Error ${modalType === "edit" ? "updating" : "saving"} college`);
      }
      console.error(`Error ${modalType === "edit" ? "updating" : "saving"} college:`, error);
      setLoading(false);
    }
  };

  const handleDelete = (college: CollegeData): void => {
    setSelectedCollege(college);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (): Promise<void> => {
    if (selectedCollege) {
      try {
        setLoading(true);
        const json = {
          college_id: selectedCollege.college_id,
        };
        const response = await authService.post("/admin/delete_college/", json);

        if (response.status !== 200 && response.status !== 201) {
          throw new Error("Failed to delete college");
        }

        await fetchColleges();
        setShowDeleteModal(false);
        setModalNewMessage("College deleted successfully");
        setShowUpdateModal(true);
        setLoading(false);
      } catch (error) {
        console.error("Error deleting college:", error);
        setError("Failed to delete college");
        setLoading(false);
      }
    }
  };

  const filteredColleges = colleges.filter(
    (college) =>
      !college.del_row &&
      (college.college_name.toLowerCase().includes(search.toLowerCase()) ||
        college.center_name.toLowerCase().includes(search.toLowerCase()) ||
        college.college_code.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div
      className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
      style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}
    >
      <div className="d-flex justify-content-between pe-3">
        <span className="d-flex justify-content-start align-items-center ps-4">
          <label>Search</label>
          <Form.Control
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "150px" }}
            className="ms-2"
            placeholder="Search colleges..."
          />
        </span>
        <button
          className="btn btn-primary"
          onClick={() => {
            handleModal("new");
          }}
        >
          + New College
        </button>
      </div>
      <div className="p-4">
        {loading && colleges.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
            <Spinner animation="border" />
          </div>
        ) : (
          <Table className="border-top mt-2">
            <thead
              style={{
                borderBottom: "1px solid black",
                borderTop: "1px solid black",
              }}
            >
              <tr>
                <td>Sl No</td>
                <td>College Name</td>
                <td>Center Name</td>
                <td>College Code</td>
                <td>Action</td>
              </tr>
            </thead>
            <tbody>
              {filteredColleges.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center">
                    No colleges found
                  </td>
                </tr>
              ) : (
                filteredColleges.map((college, index) => (
                  <tr key={college.college_id}>
                    <td>{index + 1}</td>
                    <td>{college.college_name}</td>
                    <td>{college.center_name}</td>
                    <td>{college.college_code}</td>
                    <td>
                      <FaEdit
                        className="fs-6 me-2"
                        role="button"
                        title="Edit"
                        onClick={() => {
                          handleModal("edit", college);
                        }}
                        style={{ cursor: "pointer" }}
                      />
                      <FaTrash
                        className="fs-6 me-2"
                        title="Delete"
                        role="button"
                        onClick={() => handleDelete(college)}
                        style={{ cursor: "pointer" }}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the college:{" "}
            <strong>{selectedCollege?.college_name}</strong>?
            <br />
            <small className="text-danger">
              This will also delete all branches associated with this college.
            </small>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            No
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Yes, Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add/Edit College Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === "new" ? "Add New College" : "Edit College"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {error && <p className="text-danger">{error}</p>}
          <Form>
            <Form.Group
              controlId="formCollegeName"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label className="me-2" style={{ minWidth: "150px" }}>
                College Name
              </Form.Label>
              <div style={{ width: "60%" }}>
                <Form.Control
                  type="text"
                  name="college_name"
                  value={formData.college_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 50) {
                      setFormData({ ...formData, college_name: value });
                      setError("");
                    }
                  }}
                  placeholder="Enter college name"
                  maxLength={50}
                  isInvalid={formData.college_name.length > 50}
                />
                <Form.Text className="text-muted">
                  {formData.college_name.length}/50 characters
                </Form.Text>
              </div>
            </Form.Group>

            <Form.Group
              controlId="formCenterName"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label className="me-2" style={{ minWidth: "150px" }}>
                Center Name
              </Form.Label>
              <div style={{ width: "60%" }}>
                <Form.Control
                  type="text"
                  name="center_name"
                  value={formData.center_name}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 20) {
                      setFormData({ ...formData, center_name: value });
                      setError("");
                    }
                  }}
                  placeholder="Enter center name"
                  maxLength={20}
                  isInvalid={formData.center_name.length > 20}
                />
                <Form.Text className="text-muted">
                  {formData.center_name.length}/20 characters
                </Form.Text>
              </div>
            </Form.Group>

            <Form.Group
              controlId="formCollegeCode"
              className="mt-3 d-flex align-items-center justify-content-between"
            >
              <Form.Label className="me-2" style={{ minWidth: "150px" }}>
                College Code
              </Form.Label>
              <div style={{ width: "60%" }}>
                <Form.Control
                  type="text"
                  name="college_code"
                  value={formData.college_code}
                  onChange={handleCollegeCodeChange}
                  placeholder="Enter college code (max 4 characters, letters only)"
                  maxLength={4}
                  style={{ textTransform: "uppercase" }}
                />
                <Form.Text className="text-muted">
                  {formData.college_code.length}/4 characters
                  {formData.college_code.length > 0 && formData.college_code.length < 4 && (
                    <span className="text-info ms-2">
                      (Will be padded with 'X' to make 4 characters)
                    </span>
                  )}
                  {formData.college_code.length === 4 && (
                    <span className="text-success ms-2">✓ Valid</span>
                  )}
                  <br />
                  <small className="text-muted">Letters only (A-Z), no numbers</small>
                </Form.Text>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Success Message Modal */}
      <Modal show={showUpdateModal} onHide={() => setShowUpdateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Message</Modal.Title>
        </Modal.Header>
        <Modal.Body>{modalNewMessage}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowUpdateModal(false)}>
            Okay
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Colleges;

